import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSnapshot } from 'valtio';

import Tabs, { Tab } from '@cartwhl/ui/molecules/Tabs';

import { authStore, customerStore, noticesStore, userStore } from 'stores/global';

import AccountDetails from './AccountDetails';
import DownloadHistory from './DownloadHistory';
import Favorites from './Favorites';
import MyTeam from './MyTeam';
import Overview from './Overview';
import PlanBilling from './PlanBilling';
import UploadedMedia from './UploadedMedia';

const PROFILE_PLAN_SYNC_POLL_MS = 3000;
/** Poll at t=0,3,…,30s (11 samples over the first 30 seconds). */
const PROFILE_PLAN_SYNC_POLL_ROUNDS = 11;

function ProfilePage() {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const { authStatus } = useSnapshot(authStore.state);
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle success message from payment
    if (params.has('success') && params.get('success') === 'true') {
      const newPlan = params.get('newPlan');
      const numberPurchasedSpins = params.get('numberPurchasedSpins');
      if (newPlan) {
        noticesStore.success('Thank you for signing up for Cartwheel!');
      } else if (numberPurchasedSpins) {
        const numberPurchasedSpinsInt = parseInt(numberPurchasedSpins);
        noticesStore.success(
          `You have just purchased ${numberPurchasedSpinsInt} more spins! They may take a few minutes to activate.`,
        );

        // In 5 seconds, kick off a new fetch userInfo call
        // and hopefully the new spins will be there.
        setTimeout(() => {
          userStore.load(false);
        }, 5000);
      }

      // Clean up URL by removing all query parameters
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.has('canceled') && params.get('canceled') === 'true') {
      noticesStore.info(
        "Your purchase was canceled, if there's anything we can do better, please let us know!",
      );
      // Clean up URL by removing all query parameters
      window.history.replaceState(null, '', window.location.pathname);
    }

    // This param allows us to check (and redirect) from stripe email links
    if (params.has('manageSubscription') && userInfo.stripeCustomerID) {
      userStore.manageSubscription(userInfo.stripeCustomerID);
    }
  }, [userInfo]);

  useEffect(() => {
    if (authStatus !== 'user') return undefined;

    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const syncPlanData = () => {
      void (async () => {
        await userStore.refetchUserInfo({ assignOnlyIfChanged: true });
        const stripeId = userStore.state.userInfo.stripeCustomerID;
        if (stripeId) {
          await customerStore.fetchCustomer(stripeId, {
            force: true,
            silent: true,
          });
        }
      })();
    };

    for (let i = 0; i < PROFILE_PLAN_SYNC_POLL_ROUNDS; i += 1) {
      timeouts.push(setTimeout(syncPlanData, i * PROFILE_PLAN_SYNC_POLL_MS));
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [authStatus]);

  const tabs = [
    {
      value: 'overview',
      label: 'Overview',
      content: <Overview />,
    },
    {
      value: 'favorites',
      label: 'Favorites',
      content: <Favorites />,
    },
    {
      value: 'download-history',
      label: 'Download History',
      content: <DownloadHistory />,
    },
    {
      value: 'uploaded-media',
      label: 'Uploaded videos',
      content: <UploadedMedia />,
    },
    {
      value: 'account-details',
      label: 'Account Details',
      content: <AccountDetails />,
    },
    {
      value: 'plan-billing',
      label: 'Plan & Billing',
      content: <PlanBilling />,
    },
  ];
  if (organizationInfo?.organizationID) {
    tabs.push({
      value: 'my-team',
      label: 'My Team',
      content: <MyTeam />,
    });
  }

  if (
    tab === 'my-team' &&
    ((userInfo.email && !organizationInfo) ||
      organizationInfo?.organizationID === '')
  ) {
    // If somebody loads the my-team tab but doesn't have an organizationID
    // we automatically redirect them to the profile overview page
    navigate('/profile/overview');
  }

  return (
    <main className="mt-8">
      <Tabs
        defaultValue={tab}
        className="mx-8 mb-20 lg:container"
        onChange={(tab) => navigate(`/profile/${tab}`)}
        tabs={tabs}
      ></Tabs>
    </main>
  );
}

export default ProfilePage;
